import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cva } from "class-variance-authority";
import { GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Task, TaskPriority } from "@/app/types/tasks";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/MobileToolTip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useState } from "react";
import warning from "@/assets/warning.svg";
import { useToast } from "@/components/ui/UseToast";
import { useDeleteTasksMutation } from "@/services/TasksApi";
import { ErrorType } from "@/app/types";
interface TaskCardProps {
  task: Task;
  isOverlay?: boolean;
  setOpenLog: (open: boolean) => void;
  setTaskId: (id: number | undefined) => void;
  viewKanban: string;
  onEditTask: (task: Task) => void;
}

export type TaskType = "Task";

export interface TaskDragData {
  type: TaskType;
  task: Task;
}

export function TaskCard({ onEditTask,task, isOverlay, setOpenLog, setTaskId,viewKanban }: TaskCardProps) {
    const { toast } = useToast();
  const [isdelete, setIsDelete] = useState(false);
  const [deleteTask] = useDeleteTasksMutation();
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.task_id,
    data: {
      type: "Task",
      task,
    } satisfies TaskDragData,
    attributes: {
      roleDescription: "Task",
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const variants = cva("", {
    variants: {
      dragging: {
        over: "ring-2 opacity-30",
        overlay: "ring-2 ring-primary",
      },
    },
  });
  const { data: staffData } = useGetStaffListQuery({ query: "" });
 
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    onEditTask(task);
  };

  const deleteRow = async () => {
    if (!task?.task_id) return;
      try {
        await deleteTask(task?.task_id as number)
          .unwrap()
          .then((res) => {
            // refetch();
            toast({
              variant: "success",
              title: "Task Deleted Successfully",
            });
          })
          .catch((error) => {
            toast({
              variant: "destructive",
              title: `${error?.data?.detail || error?.data?.message}`,
            });
          });
        return;
      } catch (error) {
        console.error("Error", { error });
        if (error && typeof error === "object" && "data" in error) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error in form Submission",
            description: `${typedError.data?.detail ?? (typedError.data as { message?: string }).message}`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error in form Submission",
            description: `Something Went Wrong.`,
          });
        }
      }
    };
  

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={variants({
        dragging: isOverlay ? "overlay" : isDragging ? "over" : undefined,
      })}
    >
      <CardHeader className="px-3 py-3 flex flex-row items-center  border-b-2 border-secondary relative ">
        <div>
          {viewKanban === "write" && (
            <Button
              variant={"ghost"}
              {...attributes}
              {...listeners}
              className="p-1 text-secondary-foreground/50 -ml-2 h-auto cursor-grab"
            >
              <span className="sr-only">Move task</span>
              <GripVertical />
            </Button>
          )}
        </div>
        <div className="flex-1 min-w-0 ">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="block max-w-[100px] truncate overflow-hidden whitespace-nowrap capitalize underline cursor-pointer text-base font-bold"
                onClick={() => {
                  setOpenLog(true);
                  setTaskId(task.task_id);
                }}
              >
                {task.title}
              </span>
            </TooltipTrigger>
            <TooltipContent className="capitalize max-w-[250px] w-fit break-words whitespace-normal">
              {task.title}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-row gap-2 pr-2 items-center">
          {viewKanban === "write" && (
            <button
              onClick={() => setIsDelete(true)}
              className="delete-button"
              disabled={viewKanban !== "write"}
            >
              <i className="fa fa-trash" />
            </button>
          )}
        </div>

        <div>
          <Badge
            variant="outline"
            className={cn(
              task.priority === TaskPriority.LOW
                ? "bg-[#4CAF50]"
                : task.priority === TaskPriority.MEDIUM
                  ? "bg-[#FFC107]"
                  : task.priority === TaskPriority.HIGH
                    ? "bg-[#F44336]"
                    : "",
              "ml-auto font-semibold capitalize text-sm"
            )}
          >
            {task.priority}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="px-3 pt-3 pb-6 text-left capitalize break-words whitespace-pre-line">
        <div className="flex flex-col">
          <div className="flex justify-between flex-col gap-2">
            <span className=" text-sm ">
              Assigned To :{" "}
              {staffData?.find((staff) => staff.value === task.assigned_to)
                ?.label || ""}
            </span>
            <div className="text-sm flex flex-row gap-2">
              <div>
                <span className="flex items-center gap-2 w-full text-nowrap text-sm">
                  Due Date :{" "}
                  {new Date(task.due_date)
                    .toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                    .replace(/\//g, "-")}
                </span>
              </div>

              <div className="w-full justify-end gap-2 flex items-center">
                <i className="fa-solid fa-comment"></i>
                {task.comments.length}
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={() => setIsDelete(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-start  justify-start gap-4">
                  <img src={warning} alt="warning" className="size-14" />
                  <AlertDialogTitle className="text-lg font-medium">
                    Are you sure you want to delete this Task? This action
                    cannot be undone.
                  </AlertDialogTitle>
                </div>
                <div className=" flex justify-end items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={() => setIsDelete(false)}
                    className="w-[100px] border border-primary font-semibold"
                  >
                    {""}
                    <i className="fa fa-xmark text-base px-1 "></i>
                    {""}
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteRow}
                    className="w-[100px] bg-primary !text-black font-semibold"
                  >
                    {""}
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    {""}
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
}
